import React, {useMemo, useState} from "react"
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {format, parseISO} from "date-fns";

import {
    useReactTable,
    getCoreRowModel,
} from '@tanstack/react-table'

import WorkgroupsAutocomplete from "../../components/inputs/WorkgroupsAutocomplete";
import FiltersBox from "../../components/FiltersBox";
import DataTable from "../../components/DataTable";
import MDTypography from "../../materialDashboard/components/MDTypography";

import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Icon from "@mui/material/Icon";
import BadgeStatus from "./BadgeStatus";
import WorkgroupAvatar from "../../components/columns/WorkgroupCol/WorkgroupAvatar";


const ImportTasksTable = (props) => {
    const {rows, page_count=-1, pagination, setPagination, onGlobalFilterChange, onFilterChange, sorting, onSortingChange, onDelete, dataTableId} = props;

    const {t} = useTranslation(["importTasks"]);

    const columns = useMemo(
        () => [
            {
                accessorKey: 'user',
                cell: props => <UserCol user={props.getValue()} workgroup={props.row?.original?.workgroup}/>,
                header: () => <span>{t("table.user")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    width: "15rem"
                }
            },
            {
                accessorKey: 'createdAt',
                cell: props => format(parseISO(props.getValue()), "dd/MM/yyyy HH:mm"),
                header: () => <span>{t("table.createdAt")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    width: "15rem"
                }
            },
            {
                accessorKey: 'type',
                cell: props => props.getValue(),
                header: () => <span>{t("table.type")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "10rem"
                }
            },
            {
                accessorKey: 'format',
                cell: props => props.getValue(),
                header: () => <span>{t("table.format")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "6rem"
                }
            },
            {
                accessorKey: 'status',
                cell: props => <BadgeStatus status={props.getValue()} />,
                header: () => <span>{t("table.status")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "6rem"
                }
            },
            {
                accessorKey: 'filename',
                cell: props => props.getValue(),
                header: () => <span>{t("table.file")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    width: "15rem"
                }
            },
            {
                accessorKey: 'notes',
                cell: props => props.getValue(),
                header: () => <span>{t("table.notes")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    width: "6rem"
                }
            },
            {
                accessorKey: 'Actions',
                cell: props => <ActionCol row={props.row} onDelete={onDelete}/>,
                header: () => <span>{t("table.actions")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "right"
                }
            },
        ],
        []  // eslint-disable-line react-hooks/exhaustive-deps
    )

    const defaultData = useMemo(() => [], [])

    const table = useReactTable({
        data: rows ?? defaultData,
        columns,
        pageCount: page_count ?? -1,
        state: {
            pagination,
            sorting,
        },
        onPaginationChange: setPagination,
        onGlobalFilterChange: onGlobalFilterChange,
        onSortingChange: onSortingChange,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,  // rows are expected to be paginated (controlled mode)
        manualSorting: true,  // rows are expected to be sorted (controlled mode)
        // getPaginationRowModel: getPaginationRowModel(), // If only doing manual pagination, you don't need this
        debugTable: true,
    })

    return <DataTable dataTableId={dataTableId}
                      table={table}
                      canSearch={false}
                      showRowColoring={props.showRowColoring}
                      rowColoring={props.rowColoring}
                      setRowColoring={props.setRowColoring}
                      stickyHeader={true}
                      filtersEl={<Filters onChange={onFilterChange}/>}/>
}

const ActionCol = ({row}) => {
    const navigate = useNavigate();
    const {id} = row.original;
    return <Stack direction="row" spacing={1} justifyContent="flex-end">
        <IconButton onClick={() => navigate(`/importTasks/${id}`)}>
            <Icon fontSize="small">visibility</Icon>
        </IconButton>
    </Stack>
}

const UserCol = ({user, workgroup}) => {
    return <Stack direction="row" spacing={1} justifyContent="flex-start">
        <WorkgroupAvatar workgroup={workgroup} size="xs"/>
        <MDTypography variant="body2">
            {user.name} {user.surname}
        </MDTypography>
    </Stack>
}

const Filters = ({onChange}) => {
    const [selectedWorkgroups, setSelectedWorkgroups] = useState([]);

    const filters = {
        "workgroup_id__in": selectedWorkgroups.map(w => w.id).join(',')
    }

    const handleOnApplyFilters = () => {
        onChange(filters);
    }

    return <FiltersBox onApplyFilters={handleOnApplyFilters}>
        <WorkgroupsAutocomplete selectedWorkgroups={selectedWorkgroups}
                                setSelectedWorkgroups={setSelectedWorkgroups}
                                sx={{width: "24rem"}}/>
    </FiltersBox>
}

export default ImportTasksTable;